/**
 * Dashboard Principal - Sistema de Gestión de Quirófanos Centinela Salud
 * Updated: 2025-12-29 v4 - Enhanced UX/UI
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from 'react-leaflet'
import { Icon } from 'leaflet'
import {
  Activity, Users, Clock, AlertTriangle, TrendingUp, TrendingDown,
  RefreshCw, Search, Building2, MapPin, X, Download, Eye, Phone,
  Calendar, ChevronRight, Filter, Maximize2, Minimize2, FileSpreadsheet,
  Stethoscope, Bed, UserCheck, Percent, Scissors
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts'
import toast from 'react-hot-toast'
import 'leaflet/dist/leaflet.css'
import { useAuthStore, ROLES } from '../store'

// Fix para iconos de Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Colores del tema
const COLORS = {
  alta: '#dc2626',
  media: '#f59e0b',
  baja: '#059669',
  hospital: '#047857',
  primary: '#059669',
  secondary: '#0d9488',
}

// Datos demo de hospitales - con información extendida
const DEMO_HOSPITALES = [
  {
    id: 1, nombre: 'HGZ No. 53 Los Reyes', clave: 'HGZ-53',
    latitud: 19.3647, longitud: -98.9969,
    quirofanos: 8, quirofanos_activos: 7, ocupacion: 78,
    camas_recuperacion: 24, camas_disponibles: 8,
    cirugias_hoy: 12, cirugias_programadas: 15,
    personal_guardia: 18, anestesiologos: 4,
    especialidades: ['Cirugia General', 'Traumatologia', 'Ginecologia', 'Urologia'],
    telefono: '55-5836-1200',
    director: 'Dr. Roberto Mendez Garcia'
  },
  {
    id: 2, nombre: 'HGZ No. 57 La Quebrada', clave: 'HGZ-57',
    latitud: 19.4028, longitud: -99.0236,
    quirofanos: 6, quirofanos_activos: 5, ocupacion: 85,
    camas_recuperacion: 18, camas_disponibles: 3,
    cirugias_hoy: 9, cirugias_programadas: 11,
    personal_guardia: 14, anestesiologos: 3,
    especialidades: ['Cirugia General', 'Traumatologia', 'Oncologia'],
    telefono: '55-5836-1300',
    director: 'Dra. Patricia Flores Ruiz'
  },
  {
    id: 3, nombre: 'HGZ No. 68 Tulpetlac', clave: 'HGZ-68',
    latitud: 19.5847, longitud: -99.0089,
    quirofanos: 10, quirofanos_activos: 9, ocupacion: 62,
    camas_recuperacion: 32, camas_disponibles: 14,
    cirugias_hoy: 15, cirugias_programadas: 18,
    personal_guardia: 22, anestesiologos: 5,
    especialidades: ['Cirugia General', 'Traumatologia', 'Ginecologia', 'Pediatria', 'Urologia'],
    telefono: '55-5836-1400',
    director: 'Dr. Fernando Ramirez Lopez'
  },
  {
    id: 4, nombre: 'HGZ No. 76 Xalostoc', clave: 'HGZ-76',
    latitud: 19.5234, longitud: -99.0567,
    quirofanos: 5, quirofanos_activos: 5, ocupacion: 91,
    camas_recuperacion: 15, camas_disponibles: 2,
    cirugias_hoy: 8, cirugias_programadas: 10,
    personal_guardia: 12, anestesiologos: 2,
    especialidades: ['Cirugia General', 'Traumatologia', 'Ginecologia'],
    telefono: '55-5836-1500',
    director: 'Dr. Miguel Angel Torres'
  },
  {
    id: 5, nombre: 'HGR No. 196 Ecatepec', clave: 'HGR-196',
    latitud: 19.4678, longitud: -99.0412,
    quirofanos: 12, quirofanos_activos: 11, ocupacion: 73,
    camas_recuperacion: 40, camas_disponibles: 12,
    cirugias_hoy: 18, cirugias_programadas: 22,
    personal_guardia: 28, anestesiologos: 6,
    especialidades: ['Cirugia General', 'Traumatologia', 'Ginecologia', 'Urologia', 'Oncologia', 'Neurocirugia'],
    telefono: '55-5836-1600',
    director: 'Dr. Carlos Hernandez Vega'
  },
  {
    id: 6, nombre: 'HGZ No. 197 Texcoco', clave: 'HGZ-197',
    latitud: 19.5160, longitud: -98.8830,
    quirofanos: 4, quirofanos_activos: 4, ocupacion: 55,
    camas_recuperacion: 12, camas_disponibles: 6,
    cirugias_hoy: 5, cirugias_programadas: 7,
    personal_guardia: 10, anestesiologos: 2,
    especialidades: ['Cirugia General', 'Traumatologia', 'Ginecologia'],
    telefono: '55-5836-1700',
    director: 'Dra. Ana Maria Sanchez'
  },
]

// Generar pacientes demo - cantidad aumentada
const generarPacientes = () => {
  const nombres = [
    'Laura Flores Martinez', 'Pedro Hernandez Lopez', 'Juan Martinez Garcia', 'Jose Garcia Ruiz',
    'Maria Lopez Sanchez', 'Ana Sanchez Torres', 'Carlos Ramirez Mendez', 'Sofia Torres Vega',
    'Miguel Angel Cruz', 'Rosa Mendez Ortiz', 'Fernando Ruiz Castro', 'Patricia Gomez Luna',
    'Roberto Silva Morales', 'Carmen Diaz Herrera', 'Luis Moreno Reyes', 'Elena Castro Jimenez',
    'Francisco Ortiz Ramos', 'Guadalupe Reyes Flores', 'Jorge Jimenez Torres', 'Leticia Ramos Garcia',
    'Ricardo Herrera Lopez', 'Adriana Vargas Cruz', 'Daniel Espinoza Ruiz', 'Monica Aguilar Mendez',
    'Alejandro Navarro Ortiz', 'Beatriz Romero Silva', 'Eduardo Guerrero Diaz', 'Isabel Medina Moreno',
    'Gabriel Soto Vargas', 'Teresa Dominguez Luna', 'Oscar Perez Espinoza', 'Claudia Leon Aguilar',
    'Raul Fernandez Navarro', 'Martha Rios Romero', 'Victor Alvarez Guerrero', 'Veronica Salazar Medina'
  ]
  const procedimientos = [
    'Apendicectomia', 'Colecistectomia', 'Hernioplastia inguinal', 'Cesarea', 'Artroscopia de rodilla',
    'Prostatectomia', 'Tiroidectomia', 'Mastectomia', 'Histerectomia', 'Laparoscopia diagnostica',
    'Colectomia', 'Gastrectomia', 'Nefrectomia', 'Cistoscopia', 'Reparacion de manguito rotador',
    'Artrodesis vertebral', 'Reemplazo total de cadera', 'Reemplazo total de rodilla', 'Cirugia bariatrica'
  ]
  const prioridades = ['Alta', 'Media', 'Baja']
  const especialidades = ['Cirugia General', 'Traumatologia', 'Ginecologia', 'Urologia', 'Oncologia', 'Neurocirugia']
  const estados = ['En espera', 'Pre-operatorio', 'Programado']

  return nombres.map((nombre, i) => {
    const hospital = DEMO_HOSPITALES[i % DEMO_HOSPITALES.length]
    const prioridad = prioridades[Math.floor(Math.random() * 3)]
    const diasEspera = prioridad === 'Alta' ? Math.floor(Math.random() * 60) + 30 :
      prioridad === 'Media' ? Math.floor(Math.random() * 90) + 20 :
        Math.floor(Math.random() * 120) + 10
    return {
      id: i + 1,
      nombre,
      nss: `${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      procedimiento: procedimientos[Math.floor(Math.random() * procedimientos.length)],
      especialidad: especialidades[Math.floor(Math.random() * especialidades.length)],
      prioridad,
      estado: estados[Math.floor(Math.random() * estados.length)],
      dias_espera: diasEspera,
      hospital_id: hospital.id,
      hospital_nombre: hospital.nombre,
      latitud: hospital.latitud + (Math.random() - 0.5) * 0.06,
      longitud: hospital.longitud + (Math.random() - 0.5) * 0.06,
      telefono: `55-${Math.floor(Math.random() * 9000) + 1000}-${Math.floor(Math.random() * 9000) + 1000}`,
      edad: Math.floor(Math.random() * 60) + 18,
      sexo: Math.random() > 0.5 ? 'M' : 'F',
      fecha_registro: new Date(Date.now() - diasEspera * 24 * 60 * 60 * 1000).toISOString(),
      medico_asignado: `Dr. ${['Rodriguez', 'Martinez', 'Garcia', 'Lopez', 'Hernandez'][Math.floor(Math.random() * 5)]}`,
    }
  })
}

const DEMO_PACIENTES = generarPacientes()

// Datos para gráficas
const TENDENCIA_MENSUAL = [
  { mes: 'Jul', programadas: 45, realizadas: 42, canceladas: 3 },
  { mes: 'Ago', programadas: 52, realizadas: 48, canceladas: 4 },
  { mes: 'Sep', programadas: 48, realizadas: 45, canceladas: 3 },
  { mes: 'Oct', programadas: 55, realizadas: 51, canceladas: 4 },
  { mes: 'Nov', programadas: 60, realizadas: 54, canceladas: 6 },
  { mes: 'Dic', programadas: 58, realizadas: 52, canceladas: 6 },
]

// Componente para controlar el mapa
const MapController = ({ center, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, { duration: 0.5 })
    }
  }, [center, zoom, map])
  return null
}

// Animated Counter - shows "--" when value is 0, null, or undefined
const AnimatedCounter = ({ value, duration = 1000, showDashOnZero = false }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const end = parseInt(value) || 0
    if (end === 0) {
      setCount(0)
      return
    }

    let start = 0
    const timer = setInterval(() => {
      start += Math.ceil(end / (duration / 50))
      if (start >= end) {
        setCount(end)
        clearInterval(timer)
      } else {
        setCount(start)
      }
    }, 50)

    return () => clearInterval(timer)
  }, [value, duration])

  // Show "--" when value is 0/null/undefined and showDashOnZero is true
  if (showDashOnZero && (!value || value === 0)) {
    return <span>--</span>
  }

  return <span>{count}</span>
}

// KPI Card mejorado
const KPICard = ({ title, value, subtitle, icon: IconComponent, trend, trendValue, color, onClick, showDashOnZero = false, suffix = '' }) => {
  const gradients = {
    emerald: 'from-emerald-500 via-emerald-600 to-teal-600',
    teal: 'from-teal-500 via-teal-600 to-cyan-600',
    amber: 'from-amber-500 via-orange-500 to-orange-600',
    red: 'from-red-500 via-red-600 to-rose-600',
  }

  const showDash = showDashOnZero && (!value || value === 0)

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${gradients[color]} shadow-lg text-white cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl`}
    >
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/5 rounded-full -ml-12 -mb-12" />

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm group-hover:bg-white/30 transition-colors">
            <IconComponent className="w-5 h-5" />
          </div>
          {trend && !showDash && (
            <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${trend === 'up' ? 'bg-green-400/30 text-green-100' : 'bg-red-400/30 text-red-100'
              }`}>
              {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className="text-4xl font-bold mb-1 tracking-tight">
          {showDash ? '--' : <><AnimatedCounter value={value} />{suffix}</>}
        </div>
        <div className="text-sm font-semibold opacity-95">{title}</div>
        {subtitle && <div className="text-xs opacity-75 mt-1">{subtitle}</div>}
      </div>
    </div>
  )
}

// Modal de detalle de paciente - Usa createPortal para renderizar fuera del contexto del mapa
const PatientModal = ({ patient, onClose }) => {
  if (!patient) return null

  const modalRoot = document.getElementById('modal-root')
  if (!modalRoot) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 animate-fadeIn"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp relative" style={{ zIndex: 100000, pointerEvents: 'auto' }}>
        <div className={`p-6 text-white ${patient.prioridad === 'Alta' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
          patient.prioridad === 'Media' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
            'bg-gradient-to-r from-emerald-500 to-teal-600'
          }`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{patient.nombre}</h2>
              <p className="text-white/80 text-sm mt-1">NSS: {patient.nss}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Procedimiento</p>
              <p className="font-semibold text-gray-800 mt-1">{patient.procedimiento}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Especialidad</p>
              <p className="font-semibold text-gray-800 mt-1">{patient.especialidad}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Dias en Espera</p>
              <p className={`text-2xl font-bold ${patient.dias_espera > 90 ? 'text-red-600' :
                patient.dias_espera > 60 ? 'text-amber-600' : 'text-green-600'
                }`}>{patient.dias_espera}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Prioridad</p>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${patient.prioridad === 'Alta' ? 'bg-red-100 text-red-700' :
                patient.prioridad === 'Media' ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>{patient.prioridad}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Hospital Asignado</p>
            <p className="font-semibold text-gray-800 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              {patient.hospital_nombre}
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors">
              <Calendar className="w-4 h-4" />
              Programar
            </button>
            <button className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
              <Phone className="w-4 h-4" />
              Llamar
            </button>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  )
}

// Componente principal
export default function Dashboard() {
  // Info del usuario para filtrado
  const user = useAuthStore(state => state.user)
  const isOperador = user?.rol === ROLES.OPERADOR
  const isAdmin = user?.rol === ROLES.ADMIN
  const userHospitalId = user?.hospital_id

  // Filtrar hospitales y pacientes según rol
  const hospitales = useMemo(() => {
    if (isOperador && userHospitalId) {
      return DEMO_HOSPITALES.filter(h => h.id === userHospitalId)
    }
    return DEMO_HOSPITALES
  }, [isOperador, userHospitalId])

  const pacientes = useMemo(() => {
    if (isOperador && userHospitalId) {
      return DEMO_PACIENTES.filter(p => p.hospital_id === userHospitalId)
    }
    return DEMO_PACIENTES
  }, [isOperador, userHospitalId])

  const [selectedPaciente, setSelectedPaciente] = useState(null)
  const [selectedHospital, setSelectedHospital] = useState(null)
  const [modalPaciente, setModalPaciente] = useState(null)

  // Bloquear scroll cuando modal está abierto
  useEffect(() => {
    if (modalPaciente) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [modalPaciente])

  // Centro del mapa según hospital del operador
  const defaultCenter = useMemo(() => {
    if (isOperador && userHospitalId) {
      const hosp = DEMO_HOSPITALES.find(h => h.id === userHospitalId)
      return hosp ? [hosp.latitud, hosp.longitud] : [19.45, -99.0]
    }
    return [19.45, -99.0]
  }, [isOperador, userHospitalId])

  const defaultZoom = isOperador ? 12 : 10

  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [mapZoom, setMapZoom] = useState(defaultZoom)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [mapExpanded, setMapExpanded] = useState(false)

  // Filtros
  const [filterPrioridad, setFilterPrioridad] = useState('')
  const [filterRezagados, setFilterRezagados] = useState(false)
  const [sortByEspera, setSortByEspera] = useState(false)

  useEffect(() => {
    setTimeout(() => setLoading(false), 800)
  }, [])

  // Actualizar centro del mapa cuando cambie el hospital del operador
  useEffect(() => {
    if (isOperador && userHospitalId) {
      const hosp = DEMO_HOSPITALES.find(h => h.id === userHospitalId)
      if (hosp) {
        setMapCenter([hosp.latitud, hosp.longitud])
        setMapZoom(12)
      }
    }
  }, [isOperador, userHospitalId])

  // Filtrar pacientes
  const filteredPacientes = useMemo(() => {
    let result = pacientes.filter(p => {
      const matchSearch = !searchTerm ||
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.procedimiento.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nss.includes(searchTerm)
      const matchPrioridad = !filterPrioridad || p.prioridad === filterPrioridad
      const matchSelectedHospital = !selectedHospital || p.hospital_id === selectedHospital.id
      const matchRezagados = !filterRezagados || p.dias_espera > 90
      return matchSearch && matchPrioridad && matchSelectedHospital && matchRezagados
    })

    // Ordenar por dias de espera si está activo
    if (sortByEspera) {
      result = [...result].sort((a, b) => b.dias_espera - a.dias_espera)
    }

    return result
  }, [pacientes, searchTerm, filterPrioridad, selectedHospital, filterRezagados, sortByEspera])

  // Detectar si hay filtros activos para animar los marcadores
  const hasActiveFilters = useMemo(() => {
    return !!filterPrioridad || !!selectedHospital || filterRezagados || !!searchTerm
  }, [filterPrioridad, selectedHospital, filterRezagados, searchTerm])

  // Click en paciente
  const handlePacienteClick = useCallback((paciente) => {
    setSelectedPaciente(paciente)
    setMapCenter([paciente.latitud, paciente.longitud])
    setMapZoom(14)
  }, [])

  // Click en hospital
  const handleHospitalClick = useCallback((hospital) => {
    if (selectedHospital?.id === hospital.id) {
      setSelectedHospital(null)
      setMapCenter([19.45, -99.0])
      setMapZoom(10)
    } else {
      setSelectedHospital(hospital)
      setMapCenter([hospital.latitud, hospital.longitud])
      setMapZoom(12)
    }
    setSelectedPaciente(null)
  }, [selectedHospital])

  // Reset filtros
  const handleResetFilters = useCallback(() => {
    setFilterPrioridad('')
    setFilterRezagados(false)
    setSortByEspera(false)
    setSelectedHospital(null)
    setSelectedPaciente(null)
    setSearchTerm('')
    setMapCenter([19.45, -99.0])
    setMapZoom(10)
  }, [])

  // Exportar datos
  const handleExport = useCallback(() => {
    const csvContent = [
      ['Nombre', 'NSS', 'Procedimiento', 'Prioridad', 'Dias Espera', 'Hospital'].join(','),
      ...filteredPacientes.map(p =>
        [p.nombre, p.nss, p.procedimiento, p.prioridad, p.dias_espera, p.hospital_nombre].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pacientes_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Datos exportados correctamente')
  }, [filteredPacientes])

  // Color por prioridad
  const getPrioridadColor = (prioridad) => {
    return COLORS[prioridad?.toLowerCase()] || COLORS.baja
  }

  // Stats
  const stats = useMemo(() => ({
    total: filteredPacientes.length,
    alta: filteredPacientes.filter(p => p.prioridad === 'Alta').length,
    media: filteredPacientes.filter(p => p.prioridad === 'Media').length,
    baja: filteredPacientes.filter(p => p.prioridad === 'Baja').length,
    promedioEspera: filteredPacientes.length > 0
      ? Math.round(filteredPacientes.reduce((sum, p) => sum + p.dias_espera, 0) / filteredPacientes.length)
      : 0,
    rezagados: filteredPacientes.filter(p => p.dias_espera > 90).length,
  }), [filteredPacientes])

  // Datos para gráficas
  const distribucionData = [
    { name: 'Alta', value: stats.alta, color: COLORS.alta },
    { name: 'Media', value: stats.media, color: COLORS.media },
    { name: 'Baja', value: stats.baja, color: COLORS.baja },
  ]

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-spin border-t-emerald-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="w-6 h-6 text-emerald-600 animate-pulse" />
          </div>
        </div>
        <p className="mt-4 text-gray-500 font-medium">Cargando dashboard...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            {isOperador && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {isOperador ? (
              `${pacientes.length} pacientes en tu hospital`
            ) : selectedHospital ? (
              <span className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {selectedHospital.nombre}
                <button
                  onClick={() => setSelectedHospital(null)}
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                >
                  (ver todos)
                </button>
              </span>
            ) : 'Sistema de Gestion de Quirofanos - Vista General'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exportar
          </button>
          <button
            onClick={() => window.location.reload()}
            className="p-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Búsqueda */}
          <div className="flex-1 min-w-[250px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Buscar Paciente</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Nombre, NSS o procedimiento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Hospital filter - solo para admin */}
          {!isOperador && (
            <div className="min-w-[220px]">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hospital</label>
              <select
                value={selectedHospital?.id || ''}
                onChange={(e) => {
                  const hosp = hospitales.find(h => h.id === parseInt(e.target.value))
                  if (hosp) handleHospitalClick(hosp)
                  else handleResetFilters()
                }}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all appearance-none cursor-pointer"
              >
                <option value="">Todos los hospitales</option>
                {DEMO_HOSPITALES.map(h => (
                  <option key={h.id} value={h.id}>{h.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Prioridad filter */}
          <div className="min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prioridad</label>
            <select
              value={filterPrioridad}
              onChange={(e) => setFilterPrioridad(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all appearance-none cursor-pointer"
            >
              <option value="">Todas</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </select>
          </div>

          {/* Limpiar */}
          {(selectedHospital || filterPrioridad || searchTerm || filterRezagados || sortByEspera) && (
            <button
              onClick={handleResetFilters}
              className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Pacientes en Espera"
          value={stats.total}
          subtitle="Total activos"
          icon={Users}
          trend={stats.total > 0 ? "up" : null}
          trendValue="+12%"
          color="emerald"
          onClick={() => setFilterPrioridad('')}
          showDashOnZero={true}
        />
        <KPICard
          title="Tiempo Promedio"
          value={stats.promedioEspera}
          subtitle={sortByEspera ? "Ordenando por espera" : "Dias de espera"}
          icon={Clock}
          trend={stats.total > 0 ? "down" : null}
          trendValue="-5%"
          color="teal"
          onClick={() => setSortByEspera(!sortByEspera)}
          showDashOnZero={true}
        />
        <KPICard
          title="Prioridad Alta"
          value={stats.alta}
          subtitle="Requieren atencion urgente"
          icon={AlertTriangle}
          color="amber"
          onClick={() => setFilterPrioridad(filterPrioridad === 'Alta' ? '' : 'Alta')}
          showDashOnZero={true}
        />
        <KPICard
          title="Rezagados"
          value={stats.rezagados}
          subtitle={filterRezagados ? "Filtro activo" : "Mas de 90 dias"}
          icon={Activity}
          color="red"
          onClick={() => setFilterRezagados(!filterRezagados)}
          showDashOnZero={true}
        />
      </div>

      {/* Mapa y Lista */}
      <div className={`grid gap-6 ${mapExpanded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* Mapa */}
        <div className={`${mapExpanded ? '' : 'lg:col-span-2'} bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden`}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Ubicacion de Pacientes</h3>
                <p className="text-xs text-gray-500">{filteredPacientes.length} pacientes mostrados</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-emerald-700 border-2 border-white shadow-md"></div>
                  <span className="text-gray-600 font-medium">Hospital</span>
                </div>
                <div className="w-px h-4 bg-gray-300"></div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500 shadow-sm"></div>
                  <span className="text-gray-600">Alta</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-500 shadow-sm"></div>
                  <span className="text-gray-600">Media</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm"></div>
                  <span className="text-gray-600">Baja</span>
                </div>
              </div>
              <button
                onClick={() => setMapExpanded(!mapExpanded)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title={mapExpanded ? 'Reducir' : 'Expandir'}
              >
                {mapExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div style={{ height: mapExpanded ? '600px' : '480px', position: 'relative' }} className="transition-all duration-300">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <MapController center={mapCenter} zoom={mapZoom} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />

              {/* Lineas paciente-hospital */}
              {filteredPacientes.map(paciente => {
                const hospital = hospitales.find(h => h.id === paciente.hospital_id)
                if (!hospital) return null
                return (
                  <Polyline
                    key={`line-${paciente.id}`}
                    positions={[
                      [paciente.latitud, paciente.longitud],
                      [hospital.latitud, hospital.longitud]
                    ]}
                    color={getPrioridadColor(paciente.prioridad)}
                    weight={selectedPaciente?.id === paciente.id ? 3 : 1}
                    opacity={selectedPaciente?.id === paciente.id ? 0.9 : 0.25}
                    dashArray="6,6"
                  />
                )
              })}

              {/* Hospitales */}
              {hospitales.map(hospital => {
                const isSelected = selectedHospital?.id === hospital.id
                return (
                  <CircleMarker
                    key={`hospital-${hospital.id}`}
                    center={[hospital.latitud, hospital.longitud]}
                    radius={isSelected ? 20 : 16}
                    pathOptions={{
                      fillColor: COLORS.hospital,
                      fillOpacity: 0.95,
                      color: 'white',
                      weight: isSelected ? 4 : 3,
                      className: `hospital-marker ${isSelected ? 'marker-pulse' : ''}`
                    }}
                    eventHandlers={{
                      click: () => handleHospitalClick(hospital)
                    }}
                  >
                    <Popup>
                      <div className="text-xs" style={{ width: '190px' }}>
                        <div className="flex justify-between items-center gap-2 pb-1.5 mb-1.5 border-b border-gray-100">
                          <p className="font-semibold text-gray-800 text-xs leading-tight">{hospital.nombre}</p>
                          <span className={`text-sm font-bold whitespace-nowrap ${hospital.ocupacion >= 85 ? 'text-red-500' :
                            hospital.ocupacion >= 70 ? 'text-amber-500' :
                              'text-emerald-600'
                            }`}>{hospital.ocupacion}%</span>
                        </div>
                        <div className="space-y-0.5 text-[11px] text-gray-600">
                          <div className="flex justify-between">
                            <span>Quirofanos</span>
                            <span className="font-semibold text-gray-800">{hospital.quirofanos_activos}/{hospital.quirofanos}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Camas disp.</span>
                            <span className="font-semibold text-gray-800">{hospital.camas_disponibles}/{hospital.camas_recuperacion}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Cirugias hoy</span>
                            <span className="font-semibold text-blue-600">{hospital.cirugias_hoy}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Programadas</span>
                            <span className="font-semibold text-emerald-600">{hospital.cirugias_programadas}</span>
                          </div>
                        </div>
                        <div className="border-t border-gray-100 pt-1.5 mt-1.5 flex justify-between text-[11px]">
                          <span className="text-gray-500">Pacientes espera</span>
                          <span className="font-bold text-emerald-600">{pacientes.filter(p => p.hospital_id === hospital.id).length}</span>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              })}

              {/* Pacientes */}
              {filteredPacientes.map(paciente => {
                const isSelected = selectedPaciente?.id === paciente.id
                // Clase de animación según prioridad
                const prioridadClass = `marker-${paciente.prioridad || 'media'}`
                return (
                  <CircleMarker
                    key={`paciente-${paciente.id}`}
                    center={[paciente.latitud, paciente.longitud]}
                    radius={isSelected ? 14 : 9}
                    pathOptions={{
                      fillColor: getPrioridadColor(paciente.prioridad),
                      fillOpacity: 0.95,
                      color: 'white',
                      weight: isSelected ? 3 : 2,
                      className: `${prioridadClass} ${isSelected ? 'marker-pulse' : ''}`
                    }}
                    eventHandlers={{
                      click: () => handlePacienteClick(paciente)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <h3 className="font-bold text-gray-800">{paciente.nombre}</h3>
                        <p className="text-xs text-gray-500">NSS: {paciente.nss}</p>
                        <div className="mt-2 space-y-1 text-sm">
                          <p><span className="text-gray-500">Procedimiento:</span> {paciente.procedimiento}</p>
                          <p><span className="text-gray-500">Espera:</span> <span className="font-semibold">{paciente.dias_espera} dias</span></p>
                        </div>
                        <button
                          onClick={() => setModalPaciente(paciente)}
                          className="mt-3 w-full py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Ver detalles
                        </button>
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              })}
            </MapContainer>
          </div>
        </div>

        {/* Lista de Pacientes */}
        {!mapExpanded && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ maxHeight: '580px' }}>
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Lista de Pacientes</h3>
                    <p className="text-xs text-gray-500">{filteredPacientes.length} resultados</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {filteredPacientes.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No hay pacientes</p>
                  <p className="text-gray-400 text-sm mt-1">Ajusta los filtros para ver resultados</p>
                </div>
              ) : (
                filteredPacientes.map((paciente, index) => (
                  <div
                    key={paciente.id}
                    onClick={() => handlePacienteClick(paciente)}
                    className={`p-4 border-b border-gray-50 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${selectedPaciente?.id === paciente.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
                      }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm"
                        style={{ backgroundColor: getPrioridadColor(paciente.prioridad) }}
                      >
                        {paciente.nombre.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-gray-800 truncate">{paciente.nombre}</p>
                          <span className={`shrink-0 text-xs px-2 py-1 rounded-lg font-medium ${paciente.prioridad === 'Alta' ? 'bg-red-100 text-red-700' :
                            paciente.prioridad === 'Media' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                            {paciente.prioridad}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{paciente.procedimiento}</p>
                        <div className="flex items-center gap-4 mt-1.5">
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            {paciente.dias_espera} dias
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Building2 className="w-3 h-3" />
                            {paciente.hospital_nombre?.split(' ').slice(0, 3).join(' ')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setModalPaciente(paciente)
                        }}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución por Prioridad */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
            Distribucion por Prioridad
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distribucionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={95}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {distribucionData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {distribucionData.map(item => (
              <button
                key={item.name}
                onClick={() => setFilterPrioridad(item.name)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name}: <span className="font-semibold">{item.value}</span></span>
              </button>
            ))}
          </div>
        </div>

        {/* Tendencia */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
            Tendencia de Cirugias (6 meses)
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TENDENCIA_MENSUAL}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="realizadas" stroke="#059669" fill="#059669" fillOpacity={0.3} name="Realizadas" strokeWidth={2} />
                <Area type="monotone" dataKey="programadas" stroke="#0d9488" fill="#0d9488" fillOpacity={0.2} name="Programadas" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Saturación de Hospitales - Vista expandida para operadores, compacta para admin */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <div className="w-1 h-5 bg-purple-500 rounded-full"></div>
          {isOperador ? 'Estado de tu Hospital' : 'Saturacion de Hospitales'}
        </h3>
        <div className={`grid gap-4 ${isOperador ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {hospitales.map(hospital => (
            <div
              key={hospital.id}
              onClick={() => !isOperador && handleHospitalClick(hospital)}
              className={`p-4 rounded-xl border-2 transition-all ${isOperador ? '' : 'cursor-pointer hover:shadow-md'} ${selectedHospital?.id === hospital.id || isOperador
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-100 hover:border-gray-200'
                }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-semibold text-gray-800">{hospital.nombre}</span>
                  <p className="text-xs text-gray-500">{hospital.clave}</p>
                </div>
                <span className={`text-2xl font-bold ${hospital.ocupacion >= 85 ? 'text-red-600' :
                  hospital.ocupacion >= 70 ? 'text-amber-600' : 'text-green-600'
                  }`}>{hospital.ocupacion}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-4">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${hospital.ocupacion >= 85 ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                    hospital.ocupacion >= 70 ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                      'bg-gradient-to-r from-emerald-500 to-teal-500'
                    }`}
                  style={{ width: `${hospital.ocupacion}%` }}
                />
              </div>

              {/* Info extendida para operadores o cuando se selecciona un hospital */}
              {(isOperador || selectedHospital?.id === hospital.id) ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-gray-200">
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{hospital.quirofanos_activos}/{hospital.quirofanos}</p>
                    <p className="text-xs text-gray-500">Quirofanos</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{hospital.cirugias_hoy}</p>
                    <p className="text-xs text-gray-500">Cirugias Hoy</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{hospital.camas_disponibles}/{hospital.camas_recuperacion}</p>
                    <p className="text-xs text-gray-500">Camas Disp.</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{pacientes.filter(p => p.hospital_id === hospital.id).length}</p>
                    <p className="text-xs text-gray-500">Pacientes</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{hospital.quirofanos} quirofanos</span>
                  <span>{hospital.cirugias_hoy} cirugias hoy</span>
                  <span>{pacientes.filter(p => p.hospital_id === hospital.id).length} pacientes</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal de paciente - usa createPortal internamente */}
      {modalPaciente && (
        <PatientModal patient={modalPaciente} onClose={() => setModalPaciente(null)} />
      )}
    </div>
  )
}
